import { useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { EnhancedAutocomplete, FullRow, IconButton, Typography, Row, FullColumn } from 'seagull';
import { CloseLiner24, Edit2Liner24, EyeLine, ImportLiner24, TrashLine } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from "../../constants";
import { UserRole } from "../../models";
import { selectors } from "../../store/state-selectors";
import { useStore } from "../../store/store-context";
import { useLegalTerms } from "../../hooks/reactQuery/queries/useLegalTerms";
import { useDeleteLegalTerms } from "./hooks/useDeleteLegalTerms";
import { useRenameLegalTerms } from "./hooks/useRenameLegalTerms";
import { useUploadLegalTermsWithCustomName } from "./hooks/useUploadLegalTermsWithCustomName/useUploadLegalTermsWithCustomName";
import { useViewLegalTerms } from "./hooks/useViewLegalTerms";
import styles from "./LegalTermsDropdown.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const GetLegalTermsDropdownTID = testid => getTestIds(testid);
export const LegalTermsDropdown = _ref => {
  let {
    selectedLegalTermId,
    onSelectLegalTerm,
    testid,
    disabled,
    entityLegalTerms,
    onRemoveLegalTerm,
    viewMode: initialViewMode = 'basic',
    error,
    disabledItems,
    inputRef
  } = _ref;
  const role = useStore(selectors.user.role);
  const isMember = role === UserRole.Member;
  const viewMode = isMember ? 'member' : initialViewMode;
  const isAdvancedViewMode = viewMode === 'advanced';
  const isBasicViewMode = viewMode === 'basic';
  const isMemberViewMode = viewMode === 'member';
  const {
    LegalTermsDropdownTID
  } = useMemo(() => GetLegalTermsDropdownTID(testid), [testid]);
  const history = useHistory();
  const {
    legalTerms,
    isLoading: isLoadingLegalTerms
  } = useLegalTerms();
  const {
    viewLegalTerms
  } = useViewLegalTerms();
  const {
    deleteLegalTerms,
    isPending: isDeletingLegalTerms
  } = useDeleteLegalTerms({
    onSuccess: () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    confirmActionTestId: LegalTermsDropdownTID.DeleteLegalTermConfirmModal()
  });
  const {
    renameLegalTerms,
    isPending: isRenamingLegalTerms
  } = useRenameLegalTerms({
    errorToastTestId: LegalTermsDropdownTID.RenameErrorToast()
  });
  const fileInputRef = useRef(null);
  const allLegalTerms = useMemo(() => {
    if (entityLegalTerms) {
      const existingIds = new Set(legalTerms.map(lt => lt.id));
      const uniqueEntityLegalTerms = entityLegalTerms.filter(entityLT => (entityLT === null || entityLT === void 0 ? void 0 : entityLT.id) && !existingIds.has(entityLT === null || entityLT === void 0 ? void 0 : entityLT.id));
      return [...legalTerms, ...uniqueEntityLegalTerms];
    }
    return legalTerms;
  }, [entityLegalTerms, legalTerms]);
  const {
    uploadLegalTerms,
    isLoading: isUploadingLegalTerms
  } = useUploadLegalTermsWithCustomName({
    errorToastTestId: LegalTermsDropdownTID.UploadErrorToast(),
    onSettled: newLegalTerms => {
      if (newLegalTerms) {
        onSelectLegalTerm(newLegalTerms);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  });
  const handlePDFUpload = async e => {
    e.preventDefault();
    const file = e.target.files && e.target.files[0];
    uploadLegalTerms(file);
  };
  const isLoading = [isLoadingLegalTerms, isUploadingLegalTerms, isDeletingLegalTerms, isRenamingLegalTerms].some(Boolean);
  const selectedOption = useMemo(() => {
    return selectedLegalTermId ? allLegalTerms.find(legalTerm => legalTerm.id === selectedLegalTermId) : undefined;
  }, [selectedLegalTermId, allLegalTerms]);
  return /*#__PURE__*/_jsxs(FullRow, {
    gap: 8,
    centered: true,
    children: [/*#__PURE__*/_jsx("input", {
      ref: fileInputRef,
      type: "file",
      accept: ".pdf",
      onChange: handlePDFUpload,
      "data-testid": LegalTermsDropdownTID.UploadFileInput(),
      hidden: true
    }), /*#__PURE__*/_jsx(EnhancedAutocomplete, {
      options: allLegalTerms,
      isLoading: isLoading,
      className: styles.LegalTermsDropdown,
      keyField: "id",
      labelField: "name",
      itemRenderer: data => /*#__PURE__*/_jsxs(FullRow, {
        gap: 4,
        justifyContent: "space-between",
        alignItems: "start",
        children: [/*#__PURE__*/_jsxs(FullColumn, {
          gap: 4,
          alignItems: "start",
          children: [/*#__PURE__*/_jsx(Typography, {
            skin: "secondary",
            variant: "paragraph1",
            "data-testid": LegalTermsDropdownTID.Dropdown.Item.Name(data.id),
            children: data.name
          }), /*#__PURE__*/_jsx(Typography, {
            skin: "secondary",
            variant: "paragraph2",
            "data-testid": LegalTermsDropdownTID.Dropdown.Item.PdfFileName(data.id),
            children: data.pdfFileName || data.name
          })]
        }), isAdvancedViewMode && /*#__PURE__*/_jsxs(Row, {
          children: [/*#__PURE__*/_jsx(IconButton, {
            tooltip: {
              title: 'Rename',
              placement: 'top'
            },
            size: "medium",
            onClick: e => {
              e.stopPropagation();
              renameLegalTerms(data);
            },
            "data-testid": LegalTermsDropdownTID.Dropdown.Item.EditButton(data.id),
            children: /*#__PURE__*/_jsx(Edit2Liner24, {})
          }), /*#__PURE__*/_jsx(IconButton, {
            tooltip: {
              title: 'Delete',
              placement: 'top'
            },
            size: "medium",
            onClick: e => {
              e.stopPropagation();
              deleteLegalTerms(data);
            },
            "data-testid": LegalTermsDropdownTID.Dropdown.Item.DeleteButton(data.id),
            children: /*#__PURE__*/_jsx(TrashLine, {})
          })]
        })]
      }),
      size: "medium",
      textFieldProps: {
        placeholder: 'Choose a document',
        label: 'Terms of Services',
        alwaysShrinkLabel: true,
        inputRef
      },
      selectedOption: selectedOption,
      applyOptionChange: option => {
        onSelectLegalTerm(option);
      },
      newItemOption: isMemberViewMode ? undefined : {
        text: 'Upload PDF',
        icon: /*#__PURE__*/_jsx(ImportLiner24, {}),
        onClick: async () => {
          var _fileInputRef$current;
          (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 || _fileInputRef$current.click();
        }
      },
      promotionalActionOption: isBasicViewMode ? {
        title: 'Looking to manage your Terms of Service?',
        body: 'To edit, rename or remove saved documents, go to your Default Proposal Settings.',
        onClick: () => {
          history.push(routes.proposalDefaults);
        }
      } : undefined,
      emptySearchText: "No matches found. Try again or create a new document.",
      "data-testid": LegalTermsDropdownTID.Dropdown.Root(),
      disabled: disabled,
      error: !!error,
      helperText: error,
      disabledItems: disabledItems
    }), /*#__PURE__*/_jsxs(Row, {
      gap: 0,
      children: [/*#__PURE__*/_jsx(IconButton, {
        tooltip: {
          title: 'View',
          placement: 'top'
        },
        type: "button",
        size: "medium",
        "data-testid": LegalTermsDropdownTID.ViewLegalTermsButton(),
        disabled: !selectedOption,
        onClick: () => {
          if (selectedOption) {
            viewLegalTerms(selectedOption);
          }
        },
        children: /*#__PURE__*/_jsx(EyeLine, {})
      }), onRemoveLegalTerm && /*#__PURE__*/_jsx(IconButton, {
        size: "medium",
        "data-testid": LegalTermsDropdownTID.RemoveButton(),
        onClick: () => onRemoveLegalTerm(selectedOption),
        children: /*#__PURE__*/_jsx(CloseLiner24, {})
      })]
    })]
  });
};